// frontend/src/services/auth.js

/**
 * Authentication service for token management.
 *
 * Supports two modes:
 * 1. Cookie Mode (USE_COOKIES=true): Tokens stored in httpOnly cookies
 *    - More secure (XSS protection)
 *    - Automatic multi-tab synchronization
 *    - Uses *-secure endpoints
 *
 * 2. LocalStorage Mode (USE_COOKIES=false): Tokens stored in localStorage
 *    - Legacy behavior
 *    - Uses standard endpoints
 *
 * Security:
 * - Access tokens: Short-lived (configurable, default 30 min)
 * - Refresh tokens: Long-lived (configurable, default 30 days)
 * - Token rotation: New refresh token issued on each use
 */

import api from "./api";
import { normalizeEmail } from "../utils/textUtils";
import { readCapturedFriendCode, signupFriendCodeFields } from "../utils/friendCode";
import { resolveAcquisitionCode } from "../utils/acquisition";
import { signupLegalVersionFields } from "../utils/legalVersions";

// Check if cookie-based auth is enabled
const USE_COOKIES = process.env.REACT_APP_USE_COOKIES === "true";

// Storage keys for tokens (only used in localStorage mode)
const ACCESS_TOKEN_KEY = "token";
const REFRESH_TOKEN_KEY = "refresh_token";

// API base URL for direct fetch calls
const API_BASE_URL =
  process.env.REACT_APP_API_URL || "http://localhost:8000/api/v1";

/**
 * `error.code` on the Error thrown when `logoutAllSessions({ excludeCurrent:
 * true })` is called on the legacy header-token build, which cannot express it.
 *
 * Exported so a caller can identify it without matching on the message text.
 * See the header-token branch of `logoutAllSessions` for why the flag is
 * refused there instead of ignored.
 */
export const EXCLUDE_CURRENT_UNSUPPORTED = "exclude_current_unsupported";

/**
 * `error.code` on the Error thrown when `revokeSession()` is called on the
 * legacy header-token build, which has no session list to revoke FROM.
 *
 * Same idiom as `EXCLUDE_CURRENT_UNSUPPORTED` above and the same reasoning:
 * both session routes are cookie-only (`get_current_user_from_cookie` +
 * `refresh_token` cookie), so the header-token build cannot say which row is
 * "this device" and must not be shown a list it cannot act on. `listSessions`
 * answers EMPTY there rather than throwing - an empty list is a true statement
 * and the caller renders nothing - while a revoke is a write and is refused
 * outright, before any request.
 */
export const SESSIONS_REQUIRE_COOKIES = "sessions_require_cookies";

/**
 * Tag the HTTP status of an axios rejection onto the Error, IN PLACE.
 *
 * The two `/auth/sessions` callers decide by `error.status` and never by the
 * server's detail text (English-only and deliberately static - the 404 answers
 * the same way for another user's id, a non-existent id and an already-revoked
 * one, so the route is not an oracle). Axios 1.x already sets `.status` on an
 * `AxiosError` that carries a response, but NOT on the error the shared
 * instance's own 429 branch mints (`services/api.js` builds a plain Error with
 * `.response` attached and a localized message), so the status is read back off
 * `response` whenever it is missing.
 *
 * IN PLACE, rather than a fresh Error: the interceptor's `isRateLimitError`
 * flag and the original `response` are the two things the panel's other error
 * paths read, and re-wrapping would drop both. An ABSENT status is left absent
 * - a transport failure claims nothing about what the server did, which is the
 * safe end of the caller's map.
 */
const tagSessionStatus = (error) => {
  if (error && typeof error === "object" && typeof error.status !== "number") {
    const status = error.response?.status;
    if (typeof status === "number") {
      error.status = status;
    }
  }
  return error;
};

// Public pages that don't require authentication
const PUBLIC_PATHS = [
  '/login',
  '/register',
  '/forgot-password',
  '/reset-password',
  '/verify-email',
  '/terms',
  '/privacy',
  '/cookies',
  '/'
];

/**
 * Check if current page is a public page
 */
const isPublicPage = () => {
  const path = window.location.pathname;
  return PUBLIC_PATHS.some(publicPath =>
    path === publicPath || path.startsWith(publicPath + '/')
  );
};

const authService = {
  // ============================================================================
  // Token Storage Functions
  // ============================================================================

  /**
   * Store both access and refresh tokens.
   * @param {string} accessToken - JWT access token
   * @param {string} refreshToken - Refresh token for obtaining new access tokens
   */
  setTokens(accessToken, refreshToken) {
    if (accessToken) {
      localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
    }
    if (refreshToken) {
      localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
    }
  },

  /**
   * Get the current access token.
   * @returns {string|null} Access token or null if not present
   */
  getAccessToken() {
    return localStorage.getItem(ACCESS_TOKEN_KEY);
  },

  /**
   * Get the current refresh token.
   * @returns {string|null} Refresh token or null if not present
   */
  getRefreshToken() {
    return localStorage.getItem(REFRESH_TOKEN_KEY);
  },

  /**
   * Remove all authentication tokens (logout).
   */
  removeTokens() {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
  },

  /**
   * Check if user has any token stored (may or may not be valid).
   * In cookie mode: We can't check httpOnly cookies, so we rely on API calls.
   * In localStorage mode: Check if access token exists.
   * @returns {boolean} True if access token exists (or always true in cookie mode to trigger API check)
   */
  getCurrentUser() {
    if (USE_COOKIES) {
      // In cookie mode, we can't check httpOnly cookies from JS
      // Return true to trigger an API check in AuthContext
      return true;
    }
    return localStorage.getItem(ACCESS_TOKEN_KEY) ? true : false;
  },

  /**
   * Check if using cookie-based authentication.
   * @returns {boolean} True if cookie mode is enabled
   */
  isUsingCookies() {
    return USE_COOKIES;
  },

  // ============================================================================
  // Token Validation Functions
  // ============================================================================

  /**
   * Check if the access token is expired or about to expire.
   * Uses a 5-minute buffer to allow refresh before actual expiration.
   * In cookie mode: Always returns false (let server handle expiry).
   * @returns {boolean} True if token is expired or will expire soon
   */
  isTokenExpired() {
    if (USE_COOKIES) {
      // In cookie mode, we can't check httpOnly cookies
      // Return false to let the API call determine if token is valid
      return false;
    }

    const token = localStorage.getItem(ACCESS_TOKEN_KEY);
    if (!token) return true;

    try {
      // Decode JWT payload (middle part of token)
      const payload = JSON.parse(atob(token.split(".")[1]));

      // JWT exp is in seconds, JavaScript Date uses milliseconds
      const expiryTime = payload.exp * 1000;

      // Add 5-minute buffer to allow refresh before actual expiration
      const bufferTime = 5 * 60 * 1000;

      return Date.now() >= expiryTime - bufferTime;
    } catch (error) {
      console.error("Error parsing token:", error);
      return true; // Invalid token format = treat as expired
    }
  },

  /**
   * Check if the access token is actually expired (no buffer).
   * Used for determining if we need immediate refresh.
   * In cookie mode: Always returns false (let server handle expiry).
   * @returns {boolean} True if token is actually expired
   */
  isTokenActuallyExpired() {
    if (USE_COOKIES) {
      // In cookie mode, we can't check httpOnly cookies
      return false;
    }

    const token = localStorage.getItem(ACCESS_TOKEN_KEY);
    if (!token) return true;

    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      const expiryTime = payload.exp * 1000;
      return Date.now() >= expiryTime;
    } catch (error) {
      return true;
    }
  },

  // ============================================================================
  // Token Refresh Functions
  // ============================================================================

  /**
   * Refresh the access token using the refresh token.
   * In cookie mode: Refresh token is read from httpOnly cookie by server.
   * In localStorage mode: Refresh token is sent in request body.
   *
   * @returns {Promise<string|null>} New access token or null if refresh failed
   *          (In cookie mode, returns "cookie" as indicator of success)
   */
  async refreshAccessToken() {
    if (USE_COOKIES) {
      // Cookie mode: Refresh token is in httpOnly cookie
      try {
        const response = await fetch(`${API_BASE_URL}/auth/refresh-secure`, {
          method: "POST",
          credentials: "include", // Include cookies
        });

        if (!response.ok) {
          // Refresh failed - token may be expired or invalid
          return null;
        }

        // Refresh succeeded - cookies updated by server
        return "cookie"; // Indicator that refresh succeeded
      } catch (error) {
        console.error("Error refreshing token (cookie mode):", error);
        return null;
      }
    }

    // LocalStorage mode: Original behavior
    const refreshToken = this.getRefreshToken();

    if (!refreshToken) {
      // No refresh token available
      return null;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });

      if (!response.ok) {
        // Refresh failed (token expired, revoked, or invalid)
        this.removeTokens();
        return null;
      }

      const data = await response.json();

      // Store new tokens (both are rotated on each refresh)
      this.setTokens(data.access_token, data.refresh_token);

      return data.access_token;
    } catch (error) {
      console.error("Error refreshing token:", error);
      // Don't remove tokens on network error - let user retry
      if (error.name !== "TypeError") {
        // TypeError usually means network error
        this.removeTokens();
      }
      return null;
    }
  },

  // ============================================================================
  // Authentication API Functions
  // ============================================================================

  // ============================================================================
  // Google OAuth
  // ============================================================================

  /**
   * Authenticate (or register) via Google Sign In.
   *
   * Sends the raw Google ID token to the backend for server-side verification.
   * On success the backend sets httpOnly cookies (identical to login-secure).
   *
   * @param {string} idToken - Raw Google ID token from @react-oauth/google
   * @param {"login"|"register"} [mode="login"] - Intent of this SSO attempt.
   *        "login"  - require an existing account; missing = 404 error.
   *        "register" - create a new trial account if none exists.
   *        Default is "login" so any call site that forgets to pass a mode
   *        gets the safer no-silent-signup behavior.
   * @param {Object} [options]
   * @param {Object} [options.partnerCodeFields] register mode only: the
   *        `{partner_code, partner_code_source}` pair from the sign-up form's
   *        friend-code field (`useSignupFriendCode().signupFields()`), or `{}`
   *        for "no code". When ABSENT (a caller that does not pass options),
   *        a code captured from a `?ref=` link is sent as source `link`, the
   *        same way `acquisition_code` is read from storage below.
   * @param {string} [options.acquisitionFallback] register mode only: an
   *        opaque `?s=` code to send ONLY when this tab captured none
   *        (sessionStorage 'acq_s' empty or unreadable) - the conversion popup
   *        passes its own code here. A captured code always wins (first writer
   *        wins, components/CaptureAcquisition.js), and the fallback is never
   *        written to storage, so it cannot mislabel a later visit or sign-up.
   * @returns {Promise<Object>} Response with is_new_user flag and user profile
   * @throws {Error} On HTTP error with response.status and response.data attached
   */
  async loginWithGoogle(idToken, mode = "login", options = {}) {
    const payload = { id_token: idToken, mode };

    // Friend code (partner program) - REGISTER mode only. The server uses it
    // only when this call creates the account and never fails the sign-up
    // over it. Only the two whitelisted keys are copied, so nothing else can
    // ride along. Never logged.
    if (mode === "register") {
      const fields =
        options && options.partnerCodeFields && typeof options.partnerCodeFields === "object"
          ? options.partnerCodeFields
          : signupFriendCodeFields(readCapturedFriendCode() || "", "link");
      if (fields.partner_code) {
        payload.partner_code = fields.partner_code;
        payload.partner_code_source = fields.partner_code_source;
      }
      // Legal evidence: the Terms / Privacy versions the sign-up surface
      // displayed. Recorded by the server ONLY when this call creates the
      // account; register mode is reached only through the terms-gated
      // sign-up doors (hooks/useSignupDoors.js). A login sends none - no
      // consent box is shown there.
      Object.assign(payload, signupLegalVersionFields());
    }

    // Marketing attribution (#100): forward the opaque source code captured on
    // landing (sessionStorage 'acq_s') so a Google-SSO signup is attributed to
    // its surface. Omit the field entirely when absent. Applies to both cookie
    // and localStorage modes since the payload is built once here. The rule is
    // utils/acquisition.js's, shared with the email door; the caller's fallback
    // (options.acquisitionFallback) counts in register mode only - a login
    // never attributes, so it never needs one.
    const acquisitionCode = resolveAcquisitionCode(
      mode === "register" ? options?.acquisitionFallback : null
    );
    if (acquisitionCode) {
      payload.acquisition_code = acquisitionCode;
    }

    if (USE_COOKIES) {
      // Cookie mode: use fetch with credentials so the server can set httpOnly cookies
      const response = await fetch(`${API_BASE_URL}/auth/google`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const error = new Error(errorData.detail || "Google authentication failed");
        error.response = { status: response.status, data: errorData };
        throw error;
      }

      return response.json();
    }

    // localStorage mode: use axios (tokens stored in response body handled by AuthContext)
    const response = await api.post("/auth/google", payload);
    return response.data;
  },

  /**
   * Register a new user.
   * @param {Object} userData - User registration data
   * @returns {Promise<Object>} Registration response
   */
  async register(userData) {
    const response = await api.post("/auth/register", userData);
    return response.data;
  },

  /**
   * Login user with credentials.
   * In cookie mode: Tokens stored in httpOnly cookies by server.
   * In localStorage mode: Tokens stored in localStorage.
   *
   * @param {Object} credentials - { username: email, password: password }
   * @returns {Promise<Object>} Login response with tokens (localStorage) or user info (cookie)
   */
  async login(credentials) {
    if (USE_COOKIES) {
      // Cookie mode: Use secure endpoint with credentials
      const formData = new URLSearchParams();
      formData.append("username", credentials.username);
      formData.append("password", credentials.password);

      const response = await fetch(`${API_BASE_URL}/auth/login-secure`, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: formData,
        credentials: "include", // Include cookies in request/response
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const error = new Error(errorData.detail || "Login failed");
        error.response = { status: response.status, data: errorData };
        throw error;
      }

      const data = await response.json();
      return data;
    }

    // LocalStorage mode: Original behavior
    const formData = new URLSearchParams();
    formData.append("username", credentials.username);
    formData.append("password", credentials.password);

    const response = await api.post("/auth/login", formData, {
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
    });

    // Store both tokens
    if (response.data.access_token) {
      this.setTokens(response.data.access_token, response.data.refresh_token);
    }

    return response.data;
  },

  /**
   * Logout user from current session.
   * In cookie mode: Server clears httpOnly cookies.
   * In localStorage mode: Server revokes token, then we clear localStorage.
   *
   * IT REPORTS WHAT ACTUALLY HAPPENED, and that is the whole point of this
   * method's shape. It used to swallow every outcome - a `catch {}` in the
   * cookie branch and another in the header branch, neither of them even
   * reading `response.ok` - and return `undefined` either way. So a network
   * failure, a 500 and a clean revoke were indistinguishable to the caller,
   * and the UI showed the logged-out shell for all three while the session
   * could still be alive on the server. Telling a customer they are signed out
   * when they may not be is the one thing a sign-out control must never do.
   *
   * IT STILL NEVER THROWS, and the local cleanup still happens on every path.
   * Those two are not in tension with honesty: the local half must always run
   * (a tab holding dead credentials is worse than one holding none), and a
   * rejected promise here would become an unhandled rejection in the several
   * callers that `await logout()` with no catch. The caller gets a RESULT and
   * decides what to say.
   *
   * THE FIVE OUTCOMES, and the two judgement calls in them:
   *
   *   {ok: true,  reason: "revoked"}            the server confirmed it.
   *   {ok: true,  reason: "already_ended"}      401/403. The session is gone
   *       server-side, which is what the user asked for - it is a SUCCESS, not
   *       an error, and reporting it as a failure would tell a correctly
   *       signed-out user that something went wrong.
   *   {ok: true,  reason: "nothing_to_revoke"}  header mode with no tokens to
   *       present. There was nothing to ask the server to do.
   *   {ok: false, reason: "unreachable"}        no response at all: offline,
   *       DNS, a refused connection, an aborted request. The session may well
   *       still be alive.
   *   {ok: false, reason: "refused", status}    the server was reached and
   *       answered with a status it will not act on.
   *
   * @returns {Promise<{ok: boolean, reason: string, status?: number}>} never
   *   rejects.
   */
  async logout() {
    if (USE_COOKIES) {
      // Cookie mode: call the secure logout endpoint. Raw `fetch`, so a
      // non-2xx does NOT reject - `response.ok` is the only thing that knows,
      // and not reading it was the original bug.
      try {
        const response = await fetch(`${API_BASE_URL}/auth/logout-secure`, {
          method: "POST",
          credentials: "include",
        });
        if (response.ok) return { ok: true, reason: "revoked" };
        if (response.status === 401 || response.status === 403) {
          return { ok: true, reason: "already_ended" };
        }
        return { ok: false, reason: "refused", status: response.status };
      } catch (error) {
        // `fetch` rejects only when the request never completed.
        return { ok: false, reason: "unreachable" };
      }
      // No local tokens to clear in cookie mode.
    }

    // LocalStorage mode: revoke the refresh token, then drop both.
    const refreshToken = this.getRefreshToken();
    const accessToken = this.getAccessToken();

    let result = { ok: true, reason: "nothing_to_revoke" };
    if (refreshToken && accessToken) {
      try {
        await api.post("/auth/logout", {
          refresh_token: refreshToken,
        });
        result = { ok: true, reason: "revoked" };
      } catch (error) {
        const status = error?.response?.status;
        if (status === 401 || status === 403) {
          result = { ok: true, reason: "already_ended" };
        } else if (status) {
          result = { ok: false, reason: "refused", status };
        } else {
          // axios rejects with no `response` when the request never completed.
          result = { ok: false, reason: "unreachable" };
        }
      }
    }

    // ALWAYS clear local tokens - on every outcome above, including the two
    // failures. This tab must not keep credentials it has just tried to
    // destroy.
    this.removeTokens();
    return result;
  },

  /**
   * Logout from all sessions.
   *
   * TWO SHAPES, AND THEY ARE DIFFERENT PRODUCT ACTIONS:
   *
   *   logoutAllSessions()                        -> "sign out EVERYWHERE",
   *       including this device. Byte-for-byte the historical behaviour: the
   *       cookie branch posts no body at all (the backend's
   *       `LogoutAllRequest` body is `Optional` and an absent one means
   *       `exclude_current=false`), the server clears this device's cookies,
   *       and the localStorage branch drops this device's tokens.
   *
   *   logoutAllSessions({ excludeCurrent: true }) -> "sign out my OTHER
   *       devices". Every refresh token is revoked EXCEPT the one presented on
   *       this request, the server leaves this device's cookies in place, and
   *       NOTHING local is cleared here - this tab stays signed in, which is
   *       the entire point of the flag. Callers must therefore reach this
   *       method DIRECTLY and not through `AuthContext.logoutAllSessions`,
   *       which tears this device's session down (push subscription, caches,
   *       the `lf_auth` hint) on every path.
   *
   * The exclude path is COOKIE MODE ONLY - see the header-token branch below
   * for why - and the Security panel only offers the control when
   * `isUsingCookies()` is true (`pages/settings/security/securityView.js`).
   *
   * One backend behaviour the caller cannot see and must not assume away: when
   * `exclude_current` is asked for but this request carries no usable refresh
   * cookie, the endpoint SILENTLY falls back to revoking everything and
   * clearing the cookies. The response says nothing about which happened, so
   * `revoked_count` is the only fact returned; a caller must not read it as
   * "other devices only".
   *
   * @param {Object} [options]
   * @param {boolean} [options.excludeCurrent=false] Keep THIS device signed in.
   * @returns {Promise<{message: string, revoked_count: number}>} The backend's
   *          `LogoutResponse`. `revoked_count` is the number of refresh tokens
   *          revoked - 0 is a perfectly good answer on the exclude path (no
   *          other device was signed in) and is a SUCCESS, not a failure.
   * @throws {Error} On a failed request, and - with `code ===
   *         EXCLUDE_CURRENT_UNSUPPORTED` - when `excludeCurrent` is asked for
   *         on the header-token build.
   */
  async logoutAllSessions({ excludeCurrent = false } = {}) {
    if (USE_COOKIES) {
      // Cookie mode: Call secure logout-all endpoint
      try {
        const response = await fetch(`${API_BASE_URL}/auth/logout-all-secure`, {
          method: "POST",
          credentials: "include",
          // Spread, so the default call is byte-for-byte the request this
          // method has always sent: no body, no Content-Type, no preflight.
          ...(excludeCurrent
            ? {
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ exclude_current: true }),
              }
            : {}),
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.detail || "Logout all sessions failed");
        }

        const data = await response.json();
        // Deliberately NOTHING local is cleared on either path here: the cookie
        // build keeps no client-side auth state to clear, and the server
        // decides this device's cookies. Adding a local teardown would sign
        // this tab out on the exclude path, which is the one thing it promises
        // not to do.
        return data;
      } catch (error) {
        console.error("Logout all sessions failed (cookie mode):", error);
        throw error;
      }
    }

    // LocalStorage mode: Original behavior.
    //
    // `excludeCurrent` is REFUSED here rather than ignored, and it is refused
    // BEFORE the request so nothing is revoked. On the header-token path the
    // request carries no refresh cookie, so the backend has no proof of which
    // session is "current" and falls back to revoking everything - i.e. a
    // silently-ignored flag would sign the caller out of the device they just
    // asked to keep. The error is tagged with `code` (the file's own
    // property-on-Error idiom, same shape as the `error.response` tagging
    // above) so a caller can tell it apart from a transport failure without
    // matching on message text.
    if (excludeCurrent) {
      const error = new Error(
        "exclude_current is not supported without cookie authentication",
      );
      error.code = EXCLUDE_CURRENT_UNSUPPORTED;
      throw error;
    }

    try {
      const response = await api.post("/auth/logout-all");
      // Clear local tokens after revoking all
      this.removeTokens();
      return response.data;
    } catch (error) {
      console.error("Logout all sessions failed:", error);
      // Still clear local tokens
      this.removeTokens();
      throw error;
    }
  },

  // ============================================================================
  // Session List Functions (per-session revoke - "connected devices")
  // ============================================================================

  /**
   * This account's LIVE sessions, current one first.
   *
   * COOKIE MODE ONLY, and it answers `{sessions: []}` off it rather than
   * throwing. Both `/auth/sessions` routes authenticate through
   * `get_current_user_from_cookie` and decide "which row is this device" from
   * the `refresh_token` COOKIE, so the header-token build has no way to ask the
   * question - and a list whose `is_current` flag is a guess is worse than no
   * list, because the one row a customer must never revoke is the one they are
   * holding. An empty list is a true answer there and the Security panel
   * renders nothing for it (`security/securityView.js::showDevices` hides the
   * block on the same rule that hides "sign out of other devices").
   *
   * THE SHARED AXIOS INSTANCE, not a bare `fetch`, and that is the whole
   * point: `services/api.js` carries the SILENT-REFRESH interceptor, so a 401
   * on an expired access token is refreshed and this request is retried once
   * instead of surfacing as "we could not load your devices" to a customer
   * whose session is perfectly alive. A `fetch` bypasses every interceptor.
   * The instance is same-origin with `withCredentials` bound to the cookie
   * build, which is the credential this route authenticates on, and the GET
   * carries no body.
   *
   * The backend serves at most `SESSIONS_LIST_LIMIT` (50) rows and its response
   * model is a five-field allowlist - no token hash, no IP, no user agent -
   * and `device_label` is a coarse allowlisted reduction ("Chrome on Windows"),
   * or the sentinel `"Unknown device"` the SPA maps to its own wording.
   *
   * `sessions` is normalized to an ARRAY here, at the boundary, so no caller
   * has to type-check a malformed body before mapping over it.
   *
   * @returns {Promise<{sessions: Array<{id: string, device_label: string,
   *          started_at: string, last_used_at: string|null,
   *          is_current: boolean}>}>}
   * @throws {Error} On a failed request, tagged with `status` (the HTTP status)
   *         so a caller can decide by status and never by detail text.
   */
  async listSessions() {
    if (!USE_COOKIES) {
      return { sessions: [] };
    }

    try {
      const response = await api.get("/auth/sessions");
      const data = response?.data;
      return { sessions: Array.isArray(data?.sessions) ? data.sessions : [] };
    } catch (error) {
      console.error("List sessions failed:", error);
      throw tagSessionStatus(error);
    }
  },

  /**
   * Revoke ONE of this account's live sessions - "disconnect that device".
   *
   * THE CALLER MUST DECIDE BY `error.status`, NEVER BY THE MESSAGE. The three
   * failures are distinct product outcomes and the backend's detail strings are
   * English-only and deliberately static:
   *
   *   400 - it is the CALLER'S OWN current session. The route refuses rather
   *         than honouring it: revoking it would leave this device holding
   *         cookies whose refresh token is dead, which looks signed in until
   *         the access token lapses. Logging out is the control for that.
   *   404 - not a live session of this user: someone else's id, an id that
   *         never existed, or one already revoked. ONE answer for all three on
   *         purpose, so the route is not an oracle for which ids exist.
   *   429 - the route's own `5 per minute`.
   *
   * `status` is therefore tagged onto the Error (the file's property-on-Error
   * idiom, same shape as `error.code` above), and the message is carried only
   * so a console line says something useful - it is not copy. The backend's
   * detail, for the same console purpose, stays where axios puts it
   * (`error.response?.data?.detail`).
   *
   * THE SHARED AXIOS INSTANCE, for the reason `listSessions` states: it is the
   * one path with the silent-refresh interceptor on it, so an expired access
   * token costs a refresh rather than a spurious "we could not disconnect that
   * device". The POST carries no body.
   *
   * This route issues no cookies and never clears them, so THIS device's
   * session is untouched on every path, including the 400.
   *
   * @param {string} sessionId The `id` from `listSessions()`.
   * @returns {Promise<{message: string, revoked_count: number}>} The backend's
   *          `LogoutResponse`; `revoked_count` is always 1 on success.
   * @throws {Error} Tagged with `status` on a failed request, and - with
   *         `code === SESSIONS_REQUIRE_COOKIES` - on the header-token build.
   */
  async revokeSession(sessionId) {
    if (!USE_COOKIES) {
      // Refused BEFORE the request, the `excludeCurrent` precedent: without a
      // refresh cookie the backend cannot tell this device's row from any
      // other, so there is no safe request to send.
      const error = new Error(
        "Per-session revoke is not supported without cookie authentication",
      );
      error.code = SESSIONS_REQUIRE_COOKIES;
      throw error;
    }

    try {
      const response = await api.post(
        `/auth/sessions/${encodeURIComponent(sessionId)}/revoke`,
      );
      return response?.data;
    } catch (error) {
      console.error("Session revoke failed:", error);
      throw tagSessionStatus(error);
    }
  },

  // ============================================================================
  // User Profile Functions
  // ============================================================================

  /**
   * Get current user's profile.
   * @returns {Promise<Object>} User profile data
   */
  async getProfile() {
    const response = await api.get("/users/me");
    return response.data;
  },

  /**
   * Lightweight session probe for app bootstrap.
   *
   * Unlike getProfile() (GET /users/me, which returns 401 when logged out — and
   * the browser auto-logs that failed request to the console), this hits
   * GET /auth/session, which ALWAYS returns 200 with { authenticated, user }.
   * When authenticated, `user` is the SAME enriched payload as /users/me
   * (capability flags included), so no second request is needed. Used by
   * AuthContext on first load so a logged-out visitor on a public page produces
   * no 401 console noise.
   *
   * @returns {Promise<{authenticated: boolean, user: Object|null}>}
   */
  async getSession() {
    const response = await api.get("/auth/session");
    return response.data;
  },

  /**
   * Change user's password.
   * Note: This will revoke all refresh tokens on the server.
   *
   * Both passwords travel in the request BODY. They used to be query params,
   * which put the plaintext secrets into the URL - and therefore into every
   * access log, proxy log and browser history entry along the way. Same
   * reasoning as deleteAccount() below; the endpoint now reads a JSON body.
   *
   * TWO ACCOUNT SHAPES, ONE ENDPOINT. The backend decides which applies from
   * the row itself (`PUT /users/me/password`): an account that HAS a
   * `password_hash` must state its current password (422 when the key is
   * missing, 400 when it is wrong), while a Google-only / passwordless account
   * has none to state and is authorised by its session alone - that is the
   * path by which such an account SETS its first password.
   *
   * So `current_password` is OMITTED ENTIRELY when there is none, never sent
   * as `null` or `""`. The backend's check is `if not payload.current_password`
   * for the password account and "ignored" for the passwordless one, so a
   * falsy value would read the same either way TODAY - but the key's presence
   * is the honest statement of what the caller is claiming, and an explicit
   * `null` is exactly the shape `schemas/user.py::NULL_REJECTING_UPDATE_FIELDS`
   * exists to refuse elsewhere in this API. Same omit-when-absent shape as
   * `verifyEmail`'s `registration_proof` and `deleteAccount`'s `password`.
   *
   * @param {string} [currentPassword] - Current password. Falsy = omitted,
   *        which is correct ONLY for a passwordless account.
   * @param {string} newPassword - New password
   * @returns {Promise<Object>} Response with sessions_revoked count
   */
  async changePassword(currentPassword, newPassword) {
    const payload = { new_password: newPassword };
    if (currentPassword) {
      payload.current_password = currentPassword;
    }
    const response = await api.put("/users/me/password", payload);
    return response.data;
  },

  /**
   * Delete user's account.
   *
   * The password is sent in the request BODY (not the query string) so the
   * secret never leaks into URLs / access logs. Google-SSO / passwordless
   * accounts may omit it — the authenticated session alone authorises deletion.
   * @param {string} [password] - Password for verification (password accounts only)
   * @returns {Promise<Object>} Deletion response
   */
  async deleteAccount(password) {
    const response = await api.delete("/users/me", {
      data: password ? { password } : {},
    });
    return response.data;
  },

  // ============================================================================
  // Email Verification Functions
  // ============================================================================

  /**
   * Verify email with a code.
   *
   * The address is looked up alongside the code server-side, so it is
   * normalized here for the same reason every call in the password-reset
   * section below is: users.email is stored canonical.
   *
   * `registrationProof` is the opaque, signed value /auth/register hands back
   * (see utils/registrationProof.js). Sending it proves the
   * person entering the code is the registrant; omitting it verifies the
   * mailbox but makes the backend clear the password and answer
   * `credentials_reset: true`. It is optional and OMITTED entirely when we
   * have none, so no empty string reaches the backend - and it is never
   * logged.
   *
   * @param {string} email - Address the code was sent to
   * @param {string} code - Verification code
   * @param {string} [registrationProof] - Proof from the register response
   * @returns {Promise<Object>} Verification response, incl. `credentials_reset`
   */
  async verifyEmail(email, code, registrationProof) {
    const payload = { email: normalizeEmail(email), code };
    if (registrationProof) {
      payload.registration_proof = registrationProof;
    }
    const response = await api.post("/auth/verify-email", payload);
    return response.data;
  },

  /**
   * Resend verification email.
   * @returns {Promise<Object>} Response
   */
  async resendVerification() {
    const response = await api.post("/auth/resend-verification");
    return response.data;
  },

  // ============================================================================
  // Password Reset Functions (Task 9.5B)
  // ============================================================================

  // Every call in this section normalizes the address the same way the backend's
  // `normalize_email` does (trim + lowercase, see utils/textUtils.js). The code
  // is now looked up by (email, code) rather than by code alone, so a raw
  // "Asaf@Gmail.com" would miss the row stored as "asaf@gmail.com" and read to
  // the user as a wrong code. The page normalizes at submit time too; this is
  // the belt-and-braces half, and it is idempotent.

  /**
   * Request password reset link via email.
   * Security: Always returns success (prevents email enumeration).
   *
   * @param {string} email - Email address to send reset link to
   * @returns {Promise<Object>} Response with message
   */
  async requestPasswordReset(email) {
    const response = await api.post("/auth/forgot-password", {
      email: normalizeEmail(email),
    });
    return response.data;
  },

  /**
   * Verify password reset code before allowing password entry.
   * Security: the code is scoped to ONE address - it is validated against the
   * row belonging to `email`, and repeated wrong codes invalidate it (429).
   *
   * @param {string} email - Address the code was sent to
   * @param {string} code - 6-digit verification code
   * @returns {Promise<Object>} Response with valid status
   */
  async verifyResetCode(email, code) {
    const response = await api.post("/auth/verify-reset-code", {
      email: normalizeEmail(email),
      code,
    });
    return response.data;
  },

  /**
   * Reset password using the code from the email.
   * Security: Validates the (email, code) pair, checks expiration, enforces
   * password strength. Repeated wrong codes invalidate the code (429).
   *
   * @param {string} email - Address the code was sent to
   * @param {string} token - 6-digit reset code from the email
   * @param {string} newPassword - New password (must meet requirements)
   * @param {string} confirmPassword - Confirm new password (must match)
   * @returns {Promise<Object>} Response with message and sessions_revoked count
   */
  async resetPassword(email, token, newPassword, confirmPassword) {
    const response = await api.post("/auth/reset-password", {
      email: normalizeEmail(email),
      token,
      new_password: newPassword,
      confirm_password: confirmPassword
    });
    return response.data;
  },
};

export default authService;
